import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import DraftNightHQ from '../components/draft/DraftNightHQ';
import WarRoomFilm from '../components/draft/WarRoomFilm';
import { parseCSV } from '../utils/csv';
import { CURRENT_SEASON, isPreDraftSeason } from '../config/season';
import { loadDraftOrder } from '../utils/draftOrder';
import { loadLockedKeepers } from '../utils/keepersLocked';
import { KEEPER_DOSSIERS } from '../data/keeperDossiers';
import { Reveal } from '../motion';

const YEARS = [2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];
const CURRENT = CURRENT_SEASON.year;

// CSV columns (verified across 2013-2025):
// Season, Manager, Round, Pick_Overall, Player_Name, Position, NFL_Team, Keeper_Status, Draft_Position

export default function DraftRoom() {
  const params = useParams();
  const year = parseInt(params.year, 10) || CURRENT;
  const isCurrentSeason = year === CURRENT_SEASON.year;
  const configuredCurrentPreDraft = year === CURRENT_SEASON.year && isPreDraftSeason();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headers, setHeaders] = useState([]);
  const [draftOrder, setDraftOrder] = useState(null);
  const [lockedKeeperState, setLockedKeeperState] = useState(null);
  const keeperBoard = Object.values(KEEPER_DOSSIERS);
  const isCurrentPreDraft = configuredCurrentPreDraft && picks.length === 0;

  useEffect(() => {
    setLoading(true);

    fetch(`/draft_data/${year}_draft.csv`)
      .then((r) => (r.ok ? r.text() : ''))
      .then((text) => {
        if (!text || /^\s*<!doctype html/i.test(text) || /^\s*<html/i.test(text)) {
          setPicks([]);
          setHeaders([]);
          return;
        }
        const rows = parseCSV(text);
        setPicks(rows);
        setHeaders(rows.length > 0 ? Object.keys(rows[0]) : []);
      })
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (!isCurrentSeason) {
      setDraftOrder(null);
      setLockedKeeperState(null);
      return;
    }

    loadDraftOrder(2026, { expectedRounds: 17, allowTradedPicks: true })
      .then(setDraftOrder)
      .catch(() => setDraftOrder({ published: false, isValid: false, order: [], rounds: [], errors: [] }));
    loadLockedKeepers(2026)
      .then(setLockedKeeperState)
      .catch(() => setLockedKeeperState({ published: false, isValid: false, keepers: [], errors: ['Keeper file failed to load.'] }));
  }, [isCurrentSeason]);

  // Adapter — picks the first available column name. Real CSV uses
  // Pick_Overall / Round / Player_Name / Manager / Position; the lookup
  // arrays add common variants so older or reformatted files still render.
  const overallKey = ['Pick_Overall', 'overall', 'Overall', 'pick', 'Pick', 'pick_number'].find((k) => headers.includes(k));
  const roundKey = ['Round', 'round'].find((k) => headers.includes(k));
  const playerKey = ['Player_Name', 'player', 'Player', 'player_name', 'name', 'Name'].find((k) => headers.includes(k));
  const managerKey = ['Manager', 'manager', 'team', 'Team', 'owner'].find((k) => headers.includes(k));
  const positionKey = ['Position', 'position', 'pos', 'POS'].find((k) => headers.includes(k));
  const nflTeamKey = ['NFL_Team', 'nfl_team'].find((k) => headers.includes(k));
  const keeperKey = ['Keeper_Status', 'keeper', 'Keeper', 'keeper_status'].find((k) => headers.includes(k));

  return (
    <>
      <HeroBackground variant="gold" backdrop="studio" className="px-6 md:px-14 py-14">
        <Eyebrow>DRAFT ROOM</Eyebrow>
        <h1 className="font-display font-black uppercase tracking-normal mt-3.5 leading-[0.88]" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>
          {year} <span className="text-accent-gold">DRAFT</span>
        </h1>
        {isCurrentPreDraft ? (
          <p className="mt-5 max-w-2xl text-base md:text-lg text-ink-secondary leading-relaxed italic">
            Yahoo has published the full 17-round custom pick order with last season&apos;s traded
            picks applied. Verified player selections will replace this phase when filed.
          </p>
        ) : year === 2026 ? (
          <div className="mt-5 max-w-2xl">
            <p className="text-base md:text-lg text-ink-secondary leading-relaxed italic">
              All 170 selections are filed below, round by round, with the league&apos;s ten keepers marked.
            </p>
            <Link
              to="/draft-grades"
              className="inline-flex mt-6 text-[11px] text-bg-deep bg-accent-gold font-extrabold tracking-[0.2em] border border-accent-gold px-5 py-3 hover:bg-transparent hover:text-accent-gold transition-colors"
            >
              GRADES AND THE MATH → THE REGISTRAR
            </Link>
          </div>
        ) : null}
        <div className="flex gap-2 mt-7 flex-wrap">
          {YEARS.map((y) => (
            <Link
              key={y}
              to={`/draft-room/${y}`}
              className={`text-[10px] font-extrabold tracking-[0.2em] px-3.5 py-2 border ${
                y === year
                  ? 'bg-accent-gold text-bg-base border-accent-gold'
                  : 'border-border text-ink-tertiary hover:border-accent-gold hover:text-accent-gold'
              }`}
            >
              {y}
            </Link>
          ))}
        </div>
      </HeroBackground>

      {isCurrentPreDraft && <WarRoomFilm />}

      {isCurrentPreDraft && (
        <section className="px-6 md:px-14 py-14 border-b border-border bg-bg-deep">
          <Reveal className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6 md:gap-8">
            <article className="bg-bg-surface p-6 md:p-8 border-t-[3px] border-accent-gold">
              <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold mb-3">
                NEW FOR 2026
              </div>
              <h2 className="font-display font-black uppercase leading-[0.92] tracking-normal text-ink-primary"
                  style={{ fontSize: 'clamp(32px, 4vw, 58px)' }}>
                Single-Keeper Draft.
              </h2>
              <p className="mt-4 text-sm md:text-base text-ink-secondary leading-relaxed italic">
                {CURRENT_SEASON.keeperRule.summary} {CURRENT_SEASON.keeperRule.cost}
              </p>
            </article>
            <article className="bg-bg-surface p-6 md:p-8 border-t-[3px] border-accent-gold">
              <div className="text-[10px] tracking-[0.25em] text-accent-gold font-extrabold mb-3">
                BOARD STATUS
              </div>
              <ul className="space-y-3 text-sm text-ink-secondary leading-relaxed">
                <li>All ten keepers are locked and on the board below, including Robby's round-fifteen Colston Loveland.</li>
                <li>The 17-round custom draft order is published.</li>
                <li>Last season's traded picks are applied to the board.</li>
                <li>Draft picks publish live once the board starts moving Saturday.</li>
                <li>The 2025 draft board is the useful reference for keeper cost.</li>
              </ul>
              <Link
                to="/draft-room/2025"
                className="inline-flex mt-6 text-[11px] text-accent-gold font-extrabold tracking-[0.25em] border border-accent-gold px-5 py-3 hover:bg-accent-gold hover:text-bg-deep transition-colors"
              >
                VIEW 2025 DRAFT RECEIPTS →
              </Link>
            </article>
          </Reveal>
        </section>
      )}

      <DraftResultsBoard
        year={year}
        loading={loading}
        picks={picks}
        overallKey={overallKey}
        roundKey={roundKey}
        playerKey={playerKey}
        managerKey={managerKey}
        positionKey={positionKey}
        nflTeamKey={nflTeamKey}
        keeperKey={keeperKey}
      />

      {isCurrentSeason && <DraftOrderPreview draftOrder={draftOrder} />}

      {isCurrentPreDraft && (
        <DraftNightHQ draftOrder={draftOrder} keeperBoard={keeperBoard} publishedPicks={picks} lockedKeeperState={lockedKeeperState} />
      )}

      {isCurrentPreDraft && (
        <section className="px-6 md:px-14 py-14 border-b border-border" data-testid="keeper-market-board">
          <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
            <div>
              <Eyebrow color="gold">KEEPER VALUE WATCH</Eyebrow>
              <h2 className="mt-3 font-display font-black uppercase leading-[0.95] tracking-normal text-ink-primary"
                  style={{ fontSize: 'clamp(30px, 4.2vw, 64px)' }}>
                Keeper Market Board.
              </h2>
              <p className="mt-4 max-w-3xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
                Authenticated final rosters, original 2025 draft costs, and the Aug. 19 superflex
                market now drive every manager card. Players undrafted in 2025 are valid no-pick keepers.
              </p>
            </div>
            <Link
              to="/draft-room/2025"
              className="self-start lg:self-end text-[10px] text-accent-gold font-extrabold tracking-[0.25em] border border-accent-gold px-4 py-2.5 hover:bg-accent-gold hover:text-bg-deep transition-colors"
            >
              CHECK FULL 2025 BOARD →
            </Link>
          </Reveal>

          <Reveal delay={80} className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {keeperBoard.map((dossier) => (
              <CurrentKeeperCard key={dossier.meta.slug} dossier={dossier} />
            ))}
          </Reveal>
        </section>
      )}

    </>
  );
}

function isKeeperPick(pick, keeperKey) {
  if (!keeperKey) return false;
  return ['true', 'keeper', 'yes', '1'].includes(String(pick[keeperKey]).toLowerCase());
}

function DraftResultsBoard({
  year,
  loading,
  picks,
  overallKey,
  roundKey,
  playerKey,
  managerKey,
  positionKey,
  nflTeamKey,
  keeperKey,
}) {
  const rounds = Array.from(picks.reduce((groups, pick) => {
    const round = roundKey ? pick[roundKey] : '—';
    if (!groups.has(round)) groups.set(round, []);
    groups.get(round).push(pick);
    return groups;
  }, new Map()));

  return (
    <section className="px-6 md:px-14 py-14 border-b border-border" data-testid="draft-results-board">
      {loading ? (
        <div className="text-sm text-ink-tertiary">Loading draft data…</div>
      ) : picks.length === 0 ? (
        <div className="text-sm text-ink-tertiary">No draft data found for {year}.</div>
      ) : (
        <>
          <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
            <div>
              <Eyebrow color="gold">OFFICIAL DRAFT RESULTS</Eyebrow>
              <h2
                className="mt-3 font-display font-black uppercase leading-[0.95] tracking-normal text-ink-primary"
                style={{ fontSize: 'clamp(30px, 4.2vw, 64px)' }}
              >
                The completed board.
              </h2>
              <p className="mt-4 max-w-3xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
                Every selection is preserved in draft order. Keeper picks carry their receipt directly on the board.
              </p>
            </div>
            <div className="text-[10px] tracking-[0.2em] text-accent-gold font-extrabold">
              {picks.length} PICKS · {rounds.length} ROUNDS
            </div>
          </Reveal>

          <div className="space-y-8">
            {rounds.map(([round, roundPicks]) => (
              <article key={round} data-testid="draft-round">
                <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-accent-gold/50">
                  <h3 className="font-display text-2xl md:text-3xl font-black text-accent-gold">ROUND {round}</h3>
                  <span className="text-[9px] tracking-[0.18em] text-ink-tertiary">
                    {roundPicks.length} SELECTIONS
                  </span>
                </div>
                <div className="grid grid-cols-[50px_1fr_70px] md:grid-cols-[80px_1fr_120px] gap-3.5 text-[10px] tracking-[0.2em] text-ink-tertiary pb-2">
                  <div>OVERALL</div>
                  <div>PLAYER · MANAGER</div>
                  <div>POSITION</div>
                </div>
                {roundPicks.map((pick, index) => {
                  const keeper = isKeeperPick(pick, keeperKey);
                  return (
                    <div
                      key={overallKey ? pick[overallKey] : index}
                      data-testid="draft-pick"
                      className={`grid grid-cols-[50px_1fr_70px] md:grid-cols-[80px_1fr_120px] gap-3.5 items-center py-2.5 border-b text-sm last:border-0 ${keeper ? 'border-accent-gold/40 bg-accent-gold/5' : 'border-border'}`}
                    >
                      <div className="text-accent-gold font-black">
                        {overallKey ? pick[overallKey] : index + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black">{playerKey ? pick[playerKey] : '—'}</span>
                          {keeper && (
                            <span
                              data-testid="keeper-mark"
                              className="text-[8px] leading-none tracking-[0.16em] text-bg-deep bg-accent-gold font-extrabold px-2 py-1"
                            >
                              KEEPER
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-ink-tertiary tracking-[0.1em] mt-0.5">
                          {managerKey ? pick[managerKey] : ''}
                        </div>
                      </div>
                      <div className="text-[10px] text-ink-tertiary tracking-[0.15em]">
                        {positionKey ? pick[positionKey] : ''}
                        {nflTeamKey && pick[nflTeamKey] ? ` · ${pick[nflTeamKey]}` : ''}
                      </div>
                    </div>
                  );
                })}
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function DraftOrderPreview({ draftOrder }) {
  const firstRound = draftOrder?.order?.filter((entry) => entry.round === 1) || [];
  const tradedRoundCount = draftOrder?.rounds?.filter((round) => {
    const managers = draftOrder.order.filter((entry) => entry.round === round).map((entry) => entry.manager);
    return new Set(managers).size !== managers.length;
  }).length || 0;

  return (
    <section className="px-6 md:px-14 py-14 border-b border-border" data-testid="official-draft-order">
      <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <Eyebrow color="gold">OFFICIAL YAHOO RECEIPT</Eyebrow>
          <h2 className="mt-3 font-display font-black uppercase leading-[0.95] tracking-normal text-ink-primary"
              style={{ fontSize: 'clamp(30px, 4.2vw, 64px)' }}>
            Original Slot Ownership.
          </h2>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-ink-secondary leading-relaxed italic">
            This secondary receipt preserves Yahoo&apos;s pre-draft pick ownership and traded selections.
            The completed player board above is the final draft record.
          </p>
        </div>
        {draftOrder?.isValid && (
          <div className="text-[10px] tracking-[0.2em] text-accent-gold font-extrabold">
            170 PICKS · {tradedRoundCount} TRADE-AFFECTED ROUNDS
          </div>
        )}
      </Reveal>

      {!draftOrder ? (
        <p className="text-sm text-ink-tertiary">Loading the Yahoo draft order…</p>
      ) : !draftOrder.published || !draftOrder.isValid ? (
        <p className="text-sm text-accent-red">The published Yahoo draft order could not be validated.</p>
      ) : (
        <Reveal delay={80} className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {firstRound.map((pick) => (
            <article key={pick.overall} className="bg-bg-surface p-4 border-t-[3px] border-accent-gold min-w-0">
              <div className="text-[9px] tracking-[0.2em] text-accent-gold font-extrabold">PICK {pick.pick}</div>
              <div className="mt-3 flex items-center gap-2 min-w-0">
                <ManagerAvatar name={pick.manager} size="sm" />
                <div className="min-w-0">
                  <div className="text-sm font-black truncate">{pick.manager.toUpperCase()}</div>
                  <div className="text-[9px] text-ink-tertiary truncate">{pick.teamName}</div>
                </div>
              </div>
            </article>
          ))}
        </Reveal>
      )}
    </section>
  );
}

function numericSurplus(candidate) {
  return /^[-+]?\d+$/.test(candidate.surplus) ? Number(candidate.surplus) : Number.NEGATIVE_INFINITY;
}

function CurrentKeeperCard({ dossier }) {
  const leaderName = dossier.summaryCards.find((card) => card.label === 'Current leader')?.value;
  const leader = dossier.candidates.find((candidate) => candidate.player === leaderName);
  const alternative = dossier.candidates
    .filter((candidate) => candidate.player !== leaderName && numericSurplus(candidate) > Number.NEGATIVE_INFINITY)
    .sort((first, second) => numericSurplus(second) - numericSurplus(first))[0];
  const noPickCount = dossier.candidates.filter((candidate) => candidate.cost === 'No pick').length;

  return (
    <article className="bg-bg-surface border-t-[3px] border-accent-gold">
      <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-start gap-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0 md:w-[180px] shrink-0">
          <ManagerAvatar name={dossier.meta.manager} size="sm" />
          <div className="min-w-0">
            <div className="text-lg font-black tracking-normal truncate">{dossier.meta.manager.toUpperCase()}</div>
            <div className="text-[9px] text-ink-tertiary tracking-[0.2em]">
              {dossier.candidates.length} FINAL-ROSTER PLAYERS
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] text-accent-gold tracking-[0.25em] font-extrabold mb-2">
            CURRENT READ
          </div>
          <p className="text-sm text-ink-secondary leading-relaxed italic">
            {leader
              ? leader.cost === 'No pick'
                ? `${leader.player} leads at no pick cost, preserving every 2026 draft selection.`
                : `${leader.player} leads at ${leader.cost} with ${leader.surplus} rounds of baseline value.`
              : 'Current leader unavailable.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3">
        <KeeperSnapshot label="Current leader" candidate={leader} />
        <KeeperSnapshot label="Baseline alternative" candidate={alternative} />
        <div className="p-5 min-w-0 md:border-l border-border">
          <div className="text-[9px] tracking-[0.25em] font-extrabold mb-2 text-accent-gold">RULE CHECK</div>
          <div className="text-base md:text-lg font-black leading-tight tracking-normal">No-pick options</div>
          <div className="mt-3 bg-bg-deep p-3.5">
            <div className="font-display text-4xl font-black text-accent-gold">{noPickCount}</div>
            <div className="mt-1 text-[10px] text-ink-tertiary tracking-[0.14em]">NO PICK FORFEITED</div>
          </div>
          <p className="mt-3 text-[12px] text-ink-tertiary leading-relaxed italic">
            These players went undrafted in 2025 and may be kept without losing a 2026 selection.
          </p>
        </div>
      </div>
      <div className="border-t border-border p-4">
        <Link to={`/keeper-vault/research/${dossier.meta.slug}`} className="text-[10px] text-accent-gold font-extrabold tracking-[0.2em] uppercase">
          View full current dossier →
        </Link>
      </div>
    </article>
  );
}

function KeeperSnapshot({ label, candidate }) {
  return (
    <div className="p-5 min-w-0 border-b md:border-b-0 md:border-l first:md:border-l-0 border-border">
      <div className="text-[9px] tracking-[0.25em] font-extrabold mb-2 text-accent-gold">MARKET VALUE</div>
      <div className="text-base md:text-lg font-black leading-tight tracking-normal">
        {label}
      </div>
      {candidate ? (
        <>
          <div className="mt-3 bg-bg-deep p-3.5">
            <div className="text-lg font-black leading-tight tracking-normal break-words">
              {candidate.player}
            </div>
            <div className="mt-1 text-[10px] text-ink-tertiary tracking-[0.14em]">
              {candidate.position} · {candidate.team} · {candidate.cost.toUpperCase()}
            </div>
            <div className="mt-2 text-[10px] tracking-[0.18em] font-bold uppercase text-accent-gold">
              {candidate.surplus} ROUND SURPLUS
            </div>
          </div>
          <p className="mt-3 text-[12px] text-ink-tertiary leading-relaxed italic">
            {candidate.market}
          </p>
        </>
      ) : (
        <p className="mt-3 text-[12px] text-ink-tertiary italic">No confirmed alternative.</p>
      )}
    </div>
  );
}
